import { showToast, openErrorModal } from '@vario-connect/frontend-design';
import { clearTabDirty } from '../../lib/tab-dirty.js';

// ============================================================================
// Gambio-Lister - Varianten-Kind-Grid (EAN + Gambio-Kanalpreis + Kanal-Aktiv je Kind).
//
// Lädt per gambio-variant-rows (action:'list') alle Variantenkinder des
// Listings und zeigt sie in einer Karte im Produktübersicht-Look (.go-products):
//   - SKU (Varianten-Icon + Vario-Link falls varioBaseUrl vorhanden)
//   - Achsen-Attribute (dynamische Spalten aus rows[].attrs)
//   - EAN (inline editierbar, GS1-Prüfziffer-Hinweis NUR bei Fehler)
//   - Preis (inline editierbar, in EUR)
//   - Kanal aktiv (Schalter je Variante: dem Gambio-Verkaufskanal zuweisen/abwählen)
//   - Speichern-Icon je Zeile (bulk-Speichern über „Alle speichern")
//   - „Alle aktivieren" (alle Varianten auf einmal dem Kanal zuweisen/aktivieren)
//
// Design: statische Tabelle (KEIN ag-grid) - der Slot ist ein Auto-Höhen-Inline-
// Slot ohne fixes Höhen-Layout (siehe listing-status.ts), ag-grid bräuchte eine
// feste Höhe. Styling gescoped unter `.go-products .v*` (gambio-products.css).
//
// Rückschreiben via gambio-variant-rows (action:'save'); Kanal-Zuweisung via
// gambio-variant-channel-create (create/unassign/create_all).
// Keine Browser-Dialoge (Projektregel). showToast statt alert.
// ============================================================================

export interface VariantDataCtx {
  supabaseUrl: string;
  session: { token: string };
}

export interface VariantDataArgs {
  listingId: string;
  varioBaseUrl?: string;
}

interface ChildRow {
  articleId: string;
  sku: string | null;
  attrs: Record<string, string>;
  ean: string | null;
  eanSource: 'article' | 'listing' | null;
  priceEur: number | null;
  priceId: string | null;
  onChannel: boolean;
  channelListingId: string | null;
  eanOk: boolean;
  priceOk: boolean;
  completeness: number;
}

interface ListResponse {
  rows: ChildRow[];
  channelId: string | null;
  varioBaseUrl?: string;
  parentPriceEur?: number | null;
  priceMode?: string;
  meta: {
    isVariantParent?: boolean;
    childCount?: number;
    channelResolved?: boolean;
    onChannelCount?: number;
    note?: string;
  };
}

interface SaveResult {
  articleId: string;
  ean?: { ok: boolean; message?: string };
  price?: { ok: boolean; message?: string };
}

// Inline-SVG-Icons (Lucide-Stil, currentColor) - matchen das .go-products-Design.
const SVG_ART_VARIANT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5M12 22V12"/></svg>';
const SVG_EXTERNAL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';
const SVG_SAVE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>';
const SVG_PLUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>';

export async function renderVariantDataGrid(
  host: HTMLElement,
  ctx: VariantDataCtx,
  args: VariantDataArgs,
): Promise<void> {
  host.innerHTML = `<div class="go-products vscope" style="padding-top:16px;"><div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Varianten…</div></div>`;

  let res: ListResponse;
  try {
    res = await callFn<ListResponse>(ctx, 'gambio-variant-rows', { action: 'list', listingId: args.listingId });
  } catch (e) {
    host.innerHTML = banner('error', `Varianten nicht ladbar: ${(e as Error).message}`);
    return;
  }

  const rows = res.rows ?? [];
  const varioBaseUrl = res.varioBaseUrl ?? args.varioBaseUrl ?? '';
  const channelResolved = res.meta?.channelResolved ?? (res.channelId != null);
  const parentPriceEur = typeof res.parentPriceEur === 'number' ? res.parentPriceEur : null;
  const priceMode = res.priceMode ?? 'fixed';

  if (rows.length === 0) {
    host.innerHTML = banner('info', res.meta?.note ?? 'Keine Variantenkinder gefunden (Einzelartikel oder keine Kombinationen in Vario gepflegt).');
    return;
  }

  const axisKeys = Array.from(new Set(rows.flatMap((r) => Object.keys(r.attrs))));

  host.innerHTML = `
    <div class="go-products vscope" style="padding:16px 0 4px;">
      <div class="vhead">
        <h4>
          Variantenkinder
          <span class="vcount">${rows.length} ${rows.length === 1 ? 'Variante' : 'Varianten'}</span>
        </h4>
        <div class="vhead-actions">
          <button type="button" class="tbtn" id="gvd-assign-all">${SVG_PLUS} Alle aktivieren</button>
          <button type="button" class="tbtn primary" id="gvd-save-all">${SVG_SAVE} Alle speichern</button>
        </div>
      </div>
      ${!channelResolved ? `<div class="v-banner v-banner-warn" style="margin-bottom:10px;">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div><strong>Gambio-Verkaufskanal nicht aufgelöst</strong> - Preise können nicht geschrieben werden. Verkaufskanal in Einstellungen konfigurieren.</div>
      </div>` : ''}
      ${priceMode !== 'from_options' ? `<div class="v-banner v-banner-warn" style="margin-bottom:10px;">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>Diese Ansicht rechnet im <strong>Endpreis-Modus</strong>. Der Verkaufskanal steht auf „Aufschlag" - in den Einstellungen auf „Endpreis (from_options)" umstellen, damit Abschläge (Endpreis unter dem Vaterpreis) korrekt greifen.</div>
      </div>` : ''}
      <p class="vsub">
        EAN, Auf-/Abschlag und Endpreis je Variante pflegen (EAN artikel-global in Vario, Endpreis als Vario-Basispreis) und
        Varianten dem Verkaufskanal zuweisen bzw. abwählen. Einzeln speichern/zuweisen oder alle auf einmal.
      </p>
      <div class="vcard">
        <table class="vgrid" id="gvd-table">
          <thead>
            <tr>
              <th>SKU</th>
              ${axisKeys.map((a) => `<th>${escapeHtml(a)}</th>`).join('')}
              <th style="min-width:130px;">EAN</th>
              <th style="min-width:112px;">Auf-/Abschlag (€)</th>
              <th style="min-width:96px;">Endpreis (€)</th>
              <th style="min-width:104px;">Kanal aktiv</th>
              <th class="r" style="min-width:112px;"></th>
            </tr>
          </thead>
          <tbody>
            ${rows.map((r) => renderChildRow(r, axisKeys, varioBaseUrl, parentPriceEur)).join('')}
          </tbody>
        </table>
      </div>
    </div>`;

  // Frisch gerendertes Grid = keine ungespeicherten Eingaben (Tab-Wechsel-Guard).
  clearTabDirty(host);

  wireRowActions(host, ctx, args.listingId, rows, parentPriceEur);
}

// ── Row render ────────────────────────────────────────────────────────────────

function renderChildRow(r: ChildRow, axisKeys: string[], varioBaseUrl: string, parentPriceEur: number | null): string {
  const skuLabel = escapeHtml(r.sku ?? '—');
  const articleHref = varioBaseUrl
    ? `${varioBaseUrl}/products/articles/${encodeURIComponent(r.articleId)}/detail`
    : null;
  const skuInner = articleHref
    ? `<a class="skulink" href="${escapeAttr(articleHref)}" target="_blank" rel="noopener" title="In Vario öffnen">${skuLabel} <span class="lt">${SVG_EXTERNAL}</span></a>`
    : `<span class="skulink" style="cursor:default;">${skuLabel}</span>`;
  const skuCell = `<span class="sku-cell child"><span class="arttype" title="Variante">${SVG_ART_VARIANT}</span>${skuInner}</span>`;

  // EAN-Hinweis NUR bei ungültiger EAN (kein grünes ✓ unter gültigen Feldern).
  const eanHint = r.ean && !r.eanOk ? '✗ Prüfziffer' : '';
  const eanHintCls = r.ean && !r.eanOk ? 'vhint err' : 'vhint';

  // Ohne eigenen Vario-Preis folgt die Variante dem Vaterpreis (Aufschlag 0) — dann den EFFEKTIVEN
  // Endpreis (= Vaterpreis) anzeigen statt leer/0,00. Ein eigener Preis wird unverändert gezeigt.
  const hasOwnPrice = r.priceEur != null;
  const endprice = hasOwnPrice ? r.priceEur : parentPriceEur;
  const surcharge = endprice != null && parentPriceEur != null ? Math.round((endprice - parentPriceEur) * 100) / 100 : null;
  const hasParent = parentPriceEur != null;
  // Geerbter Endpreis (kein eigener Preis) → Hinweis; Speichern schreibt ihn NICHT zurück, solange
  // unverändert (data-gvd-price-initial-Vergleich in saveRows), damit „folgt Vater" erhalten bleibt.
  const priceInitialHint = !hasOwnPrice && endprice != null ? 'folgt Vaterpreis' : '';

  return `
    <tr data-gvd-row="${escapeAttr(r.articleId)}">
      <td>${skuCell}</td>
      ${axisKeys.map((a) => `<td class="vaxis">${escapeHtml(r.attrs[a] ?? '—')}</td>`).join('')}
      <td>
        <input class="vinp vinp--ean${r.ean && !r.eanOk ? ' err' : ''}" data-gvd-ean type="text" inputmode="numeric" autocomplete="off"
               placeholder="EAN (8/13-stellig)" value="${escapeAttr(r.ean ?? '')}" />
        <div data-gvd-ean-hint class="${eanHintCls}">${eanHint}</div>
      </td>
      <td>
        <input class="vinp vinp--price" data-gvd-surcharge type="text" inputmode="decimal" autocomplete="off"
               placeholder="${hasParent ? '0,00' : '—'}" value="${surcharge != null ? escapeAttr(String(surcharge)) : ''}"
               ${hasParent ? '' : 'disabled title="Vaterpreis nicht auflösbar - nur Endpreis pflegbar"'} />
      </td>
      <td>
        <input class="vinp vinp--price" data-gvd-price type="text" inputmode="decimal" autocomplete="off"
               data-gvd-has-own="${hasOwnPrice ? '1' : '0'}"
               data-gvd-price-initial="${endprice != null ? escapeAttr(String(endprice)) : ''}"
               placeholder="0,00" value="${endprice != null ? escapeAttr(String(endprice)) : ''}" />
        <div data-gvd-price-hint class="vhint">${priceInitialHint}</div>
      </td>
      <td style="white-space:nowrap;">
        <button type="button" class="vtoggle${r.onChannel ? ' on' : ''}"
                data-gvd-toggle-channel="${escapeAttr(r.articleId)}"
                data-on="${r.onChannel ? '1' : '0'}"
                data-listing="${escapeAttr(r.channelListingId ?? '')}"
                title="${r.onChannel ? 'Variante vom Verkaufskanal abwählen' : 'Variante dem Verkaufskanal zuweisen'}">
          <span class="v-icon ${r.onChannel ? 'fa-solid fa-toggle-on' : 'fa-regular fa-toggle-off'}"></span>
          ${r.onChannel ? 'aktiv' : 'inaktiv'}
        </button>
        <span data-gvd-channel-status class="vrowstatus muted"></span>
      </td>
      <td class="r" style="white-space:nowrap;">
        <button type="button" class="act-icon act-icon--save" data-gvd-save-row="${escapeAttr(r.articleId)}" title="Diese Variante speichern" style="color:#84B933;">${SVG_SAVE}</button>
        <span data-gvd-row-status class="vrowstatus muted"></span>
      </td>
    </tr>`;
}

// ── Wire actions ──────────────────────────────────────────────────────────────

function wireRowActions(
  host: HTMLElement,
  ctx: VariantDataCtx,
  listingId: string,
  initialRows: ChildRow[],
  parentPriceEur: number | null,
): void {
  const table = host.querySelector<HTMLElement>('#gvd-table')!;

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
  const parseNum = (s: string): number | null => {
    const t = s.trim();
    if (!t) return null;
    const n = Number(t.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  // Zwei-Wege-Bindung Auf-/Abschlag <-> Endpreis ueber den Vaterpreis.
  table.addEventListener('input', (e) => {
    const inp = e.target as HTMLInputElement;
    const tr = inp.closest<HTMLElement>('tr[data-gvd-row]');
    if (!tr) return;
    const surchargeInp = tr.querySelector<HTMLInputElement>('[data-gvd-surcharge]');
    const priceInp = tr.querySelector<HTMLInputElement>('[data-gvd-price]');
    const hint = tr.querySelector<HTMLElement>('[data-gvd-price-hint]');
    if (!priceInp) return;

    if (inp.matches('[data-gvd-surcharge]') && parentPriceEur != null) {
      const s = parseNum(inp.value);
      priceInp.value = s == null ? '' : String(round2(parentPriceEur + s));
    } else if (inp.matches('[data-gvd-price]') && parentPriceEur != null && surchargeInp) {
      const p = parseNum(inp.value);
      surchargeInp.value = p == null ? '' : String(round2(p - parentPriceEur));
    }

    if (hint && (inp.matches('[data-gvd-price]') || inp.matches('[data-gvd-surcharge]'))) {
      const p = parseNum(priceInp.value);
      if (p != null && p <= 0) { hint.textContent = 'Endpreis muss > 0 sein'; hint.className = 'vhint err'; priceInp.classList.add('err'); }
      else { hint.textContent = ''; hint.className = 'vhint'; priceInp.classList.remove('err'); }
    }
  });

  // EAN-Prüfziffer-Hinweis (nur bei Fehler).
  table.addEventListener('input', (e) => {
    const inp = e.target as HTMLInputElement;
    if (!inp.matches('[data-gvd-ean]')) return;
    const tr = inp.closest<HTMLElement>('tr[data-gvd-row]');
    if (!tr) return;
    const hint = tr.querySelector<HTMLElement>('[data-gvd-ean-hint]');
    if (!hint) return;
    const v = inp.value.trim();
    if (!v) { hint.textContent = ''; hint.className = 'vhint'; inp.classList.remove('err'); return; }
    const vr = validateEan(v);
    if (!vr.ok) {
      hint.textContent = vr.reason ?? 'ungültig';
      hint.className = 'vhint err';
      inp.classList.add('err');
    } else {
      // Gültig → kein grünes ✓, Fehler-Zustand zurücksetzen.
      hint.textContent = '';
      hint.className = 'vhint';
      inp.classList.remove('err');
    }
  });

  // Einzel-Zeile speichern.
  table.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-gvd-save-row]');
    if (!btn) return;
    const articleId = btn.dataset['gvdSaveRow']!;
    void saveRows(host, ctx, listingId, [articleId], table);
  });

  // Kanal-Schalter je Zeile (zweistufig: Erst-Klick fragt, Zweit-Klick führt aus).
  table.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-gvd-toggle-channel]');
    if (!btn) return;
    const articleId = btn.dataset['gvdToggleChannel']!;
    const isOn = btn.dataset['on'] === '1';
    const varioListingId = btn.dataset['listing'] || null;
    const statusEl = btn.parentElement?.querySelector<HTMLElement>('[data-gvd-channel-status]') ?? null;
    if (btn.dataset['armed'] !== '1') {
      btn.dataset['armed'] = '1';
      if (statusEl) { statusEl.textContent = isOn ? 'nochmal klicken: abwählen' : 'nochmal klicken: zuweisen'; statusEl.className = 'vrowstatus muted'; }
      window.setTimeout(() => { if (btn.dataset['armed'] === '1') { btn.dataset['armed'] = '0'; if (statusEl) statusEl.textContent = ''; } }, 4000);
      return;
    }
    btn.dataset['armed'] = '0';
    void toggleChannel(host, ctx, listingId, articleId, isOn, varioListingId, btn, statusEl);
  });

  // Alle zuweisen (zweistufig über den Button-Text).
  const assignAllBtn = host.querySelector<HTMLButtonElement>('#gvd-assign-all');
  assignAllBtn?.addEventListener('click', () => {
    if (assignAllBtn.dataset['armed'] !== '1') {
      assignAllBtn.dataset['armed'] = '1';
      assignAllBtn.innerHTML = `<span class="v-icon fa-solid fa-triangle-exclamation"></span> Wirklich alle ${initialRows.length} aktivieren?`;
      window.setTimeout(() => { if (assignAllBtn.dataset['armed'] === '1') { assignAllBtn.dataset['armed'] = '0'; assignAllBtn.innerHTML = `${SVG_PLUS} Alle aktivieren`; } }, 4000);
      return;
    }
    assignAllBtn.dataset['armed'] = '0';
    void assignAll(host, ctx, listingId, assignAllBtn);
  });

  // Alle speichern.
  host.querySelector<HTMLButtonElement>('#gvd-save-all')?.addEventListener('click', () => {
    const allIds = initialRows.map((r) => r.articleId);
    void saveRows(host, ctx, listingId, allIds, table);
  });
}

async function saveRows(
  host: HTMLElement,
  ctx: VariantDataCtx,
  listingId: string,
  articleIds: string[],
  table: HTMLElement,
): Promise<void> {
  const rows: Array<{ articleId: string; ean?: string; priceEur?: number }> = [];
  for (const articleId of articleIds) {
    const tr = table.querySelector<HTMLElement>(`tr[data-gvd-row="${cssEscape(articleId)}"]`);
    if (!tr) continue;
    const ean = tr.querySelector<HTMLInputElement>('[data-gvd-ean]')?.value.trim() ?? '';
    const priceInp = tr.querySelector<HTMLInputElement>('[data-gvd-price]');
    const priceStr = priceInp?.value.trim() ?? '';
    // Initial angezeigter Endpreis. Bei geerbtem Preis (kein eigener) ist das der Vaterpreis — bleibt er
    // unverändert, NICHT zurückschreiben (sonst würde ein eigener Kindpreis angelegt und „folgt Vater"
    // ginge verloren). Nur ein tatsächlich geänderter Wert wird als eigener Preis persistiert.
    const priceInitial = priceInp?.dataset['gvdPriceInitial'] ?? '';
    const priceChanged = priceStr !== priceInitial;
    if (ean && !validateEan(ean).ok) {
      const statusEl = tr.querySelector<HTMLElement>('[data-gvd-row-status]');
      if (statusEl) { statusEl.textContent = 'EAN ungültig'; statusEl.className = 'vrowstatus err'; }
      continue;
    }
    const entry: { articleId: string; ean?: string; priceEur?: number } = { articleId };
    if (ean) entry.ean = ean;
    if (priceStr && priceChanged) {
      const p = Number(priceStr.replace(',', '.'));
      if (Number.isFinite(p)) {
        if (p <= 0) {
          const statusEl = tr.querySelector<HTMLElement>('[data-gvd-row-status]');
          if (statusEl) { statusEl.textContent = 'Endpreis muss > 0 sein'; statusEl.className = 'vrowstatus err'; }
          continue;
        }
        entry.priceEur = p;
      }
    }
    if (!entry.ean && entry.priceEur === undefined) {
      // Nichts zu tun für diese Zeile.
      const statusEl = table.querySelector<HTMLElement>(`tr[data-gvd-row="${cssEscape(articleId)}"] [data-gvd-row-status]`);
      if (statusEl) { statusEl.textContent = '—'; statusEl.className = 'vrowstatus muted'; }
      continue;
    }
    rows.push(entry);
  }

  if (rows.length === 0) {
    showToast('Nichts zu speichern.', 'info');
    return;
  }

  // Buttons deaktivieren + Status setzen.
  for (const articleId of articleIds) {
    const btn = table.querySelector<HTMLButtonElement>(`button[data-gvd-save-row="${cssEscape(articleId)}"]`);
    if (btn) btn.disabled = true;
    const statusEl = table.querySelector<HTMLElement>(`tr[data-gvd-row="${cssEscape(articleId)}"] [data-gvd-row-status]`);
    if (statusEl) { statusEl.textContent = 'Speichere…'; statusEl.className = 'vrowstatus muted'; }
  }
  const saveAllBtn = host.querySelector<HTMLButtonElement>('#gvd-save-all');
  if (saveAllBtn) saveAllBtn.disabled = true;

  try {
    const res = await callFn<{ results: SaveResult[] }>(ctx, 'gambio-variant-rows', {
      action: 'save',
      listingId,
      rows,
    });

    let anyError = false;
    const errItems: Array<{ label: string; message: string }> = [];
    for (const r of res.results ?? []) {
      const tr = table.querySelector<HTMLElement>(`tr[data-gvd-row="${cssEscape(r.articleId)}"]`);
      const statusEl = tr?.querySelector<HTMLElement>('[data-gvd-row-status]') ?? null;
      const eanOk = r.ean?.ok ?? true;
      const priceOk = r.price?.ok ?? true;
      const ok = eanOk && priceOk;
      if (!ok) {
        anyError = true;
        const sku = tr?.querySelector<HTMLElement>('.skulink')?.textContent?.trim() || r.articleId;
        if (r.ean && !r.ean.ok) errItems.push({ label: `${sku} · EAN`, message: r.ean.message ?? 'unbekannter Fehler' });
        if (r.price && !r.price.ok) errItems.push({ label: `${sku} · Preis`, message: r.price.message ?? 'unbekannter Fehler' });
      }
      if (statusEl) {
        // Inline nur ein kurzer Marker - die (ggf. langen) Details laufen sonst aus der Zelle; sie stehen im Popup.
        statusEl.textContent = ok ? 'gespeichert ✓' : 'Fehler ✗';
        statusEl.className = ok ? 'vrowstatus ok' : 'vrowstatus err';
      }
    }
    if (anyError) {
      openErrorModal({
        title: 'Fehler beim Speichern',
        intro: 'Diese Varianten konnten nicht (vollständig) gespeichert werden:',
        items: errItems,
        context: { action: 'gambio-variant-rows/save', listingId, endpoint: `${ctx.supabaseUrl}/functions/v1/gambio-variant-rows`, sentRows: rows },
        json: res,
      });
    }
    showToast(
      anyError
        ? `${rows.length === 1 ? 'Variante' : `${rows.length} Varianten`} mit Fehlern - Details im Popup.`
        : `${rows.length === 1 ? 'Variante' : `${rows.length} Varianten`} gespeichert.`,
      anyError ? 'error' : 'success',
    );
    // Nach erfolgreichem Speichern den „ungespeichert"-Marker löschen (Tab-Wechsel-Guard). Bei
    // Teil-Speicherung (Einzelzeile) kann eine weitere unveränderte Zeile theoretisch noch offen
    // sein; ein erneuter Tastendruck markiert den Tab ohnehin sofort wieder.
    if (!anyError) clearTabDirty(host);
  } catch (e) {
    openErrorModal({
      title: 'Speichern fehlgeschlagen',
      items: [{ message: (e as Error).message }],
      context: { action: 'gambio-variant-rows/save', listingId, endpoint: `${ctx.supabaseUrl}/functions/v1/gambio-variant-rows`, sentRows: rows },
      json: { error: (e as Error).message, stack: (e as Error).stack },
    });
    showToast('Speichern fehlgeschlagen - Details im Popup.', 'error');
    for (const articleId of articleIds) {
      const statusEl = table.querySelector<HTMLElement>(`tr[data-gvd-row="${cssEscape(articleId)}"] [data-gvd-row-status]`);
      if (statusEl) { statusEl.textContent = ''; }
    }
  } finally {
    for (const articleId of articleIds) {
      const btn = table.querySelector<HTMLButtonElement>(`button[data-gvd-save-row="${cssEscape(articleId)}"]`);
      if (btn) btn.disabled = false;
    }
    if (saveAllBtn) saveAllBtn.disabled = false;
  }
}

// ── Kanal-Zuweisung je Variante ─────────────────────────────────────────────

async function toggleChannel(
  host: HTMLElement, ctx: VariantDataCtx, parentListingId: string,
  childArticleId: string, currentlyOn: boolean, varioListingId: string | null,
  btn: HTMLButtonElement, statusEl: HTMLElement | null,
): Promise<void> {
  btn.disabled = true;
  if (statusEl) { statusEl.textContent = currentlyOn ? 'Wähle ab…' : 'Weise zu…'; statusEl.className = 'vrowstatus muted'; }
  try {
    if (currentlyOn) {
      await callFn<unknown>(ctx, 'gambio-variant-channel-create', {
        action: 'unassign', parentListingId, childArticleId, varioListingId,
      });
    } else {
      await callFn<unknown>(ctx, 'gambio-variant-channel-create', {
        action: 'create', parentListingId, childArticleId,
      });
    }
    showToast(currentlyOn ? 'Variante vom Kanal abgewählt.' : 'Variante dem Kanal zugewiesen.', 'success');
    // Grid neu laden (Status + Bild-Matrix-Konsistenz).
    await renderVariantDataGrid(host, ctx, { listingId: parentListingId });
  } catch (e) {
    if (statusEl) { statusEl.textContent = (e as Error).message; statusEl.className = 'vrowstatus err'; }
    showToast(`Kanal-Zuweisung fehlgeschlagen: ${(e as Error).message}`, 'error');
    btn.disabled = false;
  }
}

async function assignAll(
  host: HTMLElement, ctx: VariantDataCtx, parentListingId: string, btn: HTMLButtonElement,
): Promise<void> {
  btn.disabled = true;
  btn.innerHTML = `<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Weise alle zu…`;
  try {
    const res = await callFn<{ total: number; created: number; alreadyOn: number; failed: Array<{ childArticleId: string; error: string }> }>(
      ctx, 'gambio-variant-channel-create', { action: 'create_all', parentListingId },
    );
    const failN = res.failed?.length ?? 0;
    showToast(
      failN
        ? `${res.created} zugewiesen, ${res.alreadyOn} bereits aktiv, ${failN} fehlgeschlagen.`
        : `Alle ${res.total} Varianten zugewiesen (${res.created} neu, ${res.alreadyOn} bereits aktiv).`,
      failN ? 'error' : 'success',
    );
    await renderVariantDataGrid(host, ctx, { listingId: parentListingId });
  } catch (e) {
    showToast(`Alle aktivieren fehlgeschlagen: ${(e as Error).message}`, 'error');
    btn.disabled = false;
    btn.innerHTML = `${SVG_PLUS} Alle aktivieren`;
  }
}

// ── EAN-Validierung (GS1: 8/12/13/14 Ziffern + Prüfziffer) ──────────────────

function validateEan(ean: string): { ok: boolean; reason?: string } {
  if (!/^\d+$/.test(ean)) return { ok: false, reason: 'nur Ziffern' };
  if (![8, 12, 13, 14].includes(ean.length)) return { ok: false, reason: `${ean.length} Stellen (8/13)` };
  const d = ean.split('').map(Number);
  const check = d.pop()!;
  let sum = 0;
  for (let i = d.length - 1, w = 3; i >= 0; i -= 1, w = w === 3 ? 1 : 3) sum += d[i]! * w;
  if ((10 - (sum % 10)) % 10 !== check) return { ok: false, reason: 'Prüfziffer' };
  return { ok: true };
}

// ── Shared helpers ────────────────────────────────────────────────────────────

async function callFn<T>(
  ctx: { supabaseUrl: string; session: { token: string } },
  name: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ctx.session.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    let msg = `${name} ${res.status}`;
    try { const j = JSON.parse(t); if (j?.error?.message) msg = j.error.message; } catch { /* keep */ }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

function banner(kind: 'warn' | 'error' | 'info', msg: string): string {
  const cls = kind === 'error' ? 'v-banner-danger' : kind === 'warn' ? 'v-banner-warn' : 'v-banner-info';
  const icon = kind === 'error' ? 'fa-circle-xmark' : kind === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info';
  return `<div class="v-pa-md"><div class="v-banner ${cls}"><span class="v-icon fa-light ${icon}"></span><div>${escapeHtml(msg)}</div></div></div>`;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function escapeAttr(s: string): string { return escapeHtml(s); }

function cssEscape(s: string): string {
  return s.replace(/["\\]/g, '\\$&');
}
